import { Trans, useLingui } from '@lingui/react/macro'
import { Link, useLocation, useNavigate } from '@tanstack/react-router'
import { LucideIcon } from 'components/LucideIcon'
import { Box } from 'designSystem/components/Box'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  useSidebar,
} from 'designSystem/components/Sidebar'
import { useCmmsExplorerSiteConfig } from 'features/cmmsExplorer/useCmmsExplorerSiteConfig'
import {
  NotificationPreferencesModal,
  NotificationsDropdownSidebar,
} from 'features/forecastNotifications'
import { DocumentsBugReportButton } from 'features/knowledgeDocumentViewer/components/bugReport/DocumentsBugReportButton'
import type { ViewerBugReportContext } from 'features/knowledgeDocumentViewer/utils/viewerBugReport'
import { VIEWER_BUG_REPORT_ALLOWED_ROLES } from 'features/knowledgeDocumentViewer/utils/viewerBugReport'
import { ConfigSettingsSidebar } from 'features/navbar/configSettings/ConfigSettingsSidebar'
import { WORKSPACE_SETTINGS_DESTINATION } from 'features/navbar/configSettings/workspaceSettingsLink'
import { getSelectedTab } from 'features/navbar/getSelectedTab'
import { ProfileAndSettingsSidebar } from 'features/navbar/profileAndSettings/ProfileAndSettingsSidebar'
import {
  shouldShowEmbeddedSidebarBrand,
  useSiteSelectorCapabilities,
} from 'features/navbar/siteSelectors/siteSelectorCapabilities'
import { SiteSelectorsSidebar } from 'features/navbar/siteSelectors/SiteSelectorsSidebar'
import { SelectedTab } from 'features/navbar/types'
import { useNavbarConfig } from 'features/navbar/useNavbarConfig'
import { useNavbarData } from 'features/navbar/useNavbarData'
import { useSelectedOrgAndSiteInterface } from 'features/orgAndSiteSourceOfTruth'
import { useCanVisitSiteView } from 'features/overview'
import { PermissionsGate, useGeckoPermissions } from 'features/permissions'
import { useOptionalWorkspaceTabs } from 'features/workspaceTabs/contexts/WorkspaceTabsContext'
import { type MouseEvent, useCallback, useState } from 'react'
import { SEARCH_PARAM_SITE_ID } from 'routes/(global_search_params)/-searchParams'
import { PERMISSION_GECKO_INTERNAL_MEMBER } from 'types'

import { EmbeddedSidebarChatHistory } from './chatHistory/EmbeddedSidebarChatHistory'
import { withCmmsExplorerNavItem } from './cmmsSidebarNav'
import { EmbeddedSidebarFooterBar } from './components/EmbeddedSidebarFooterBar'
import { EmbeddedSidebarHomeNav } from './components/EmbeddedSidebarHomeNav'
import { EmbeddedSidebarPagesSplitHandle } from './components/EmbeddedSidebarPagesSplitHandle'
import { SidebarPagesPane } from './components/embeddedSidebarStyles'
import { getVisibleSidebarNavItems } from './components/getVisibleSidebarNavItems'
import { SidebarNewChatButton } from './components/SidebarNewChatButton'
import { SidebarSearchMenuRow } from './components/SidebarSearchMenuRow'
import { useSidebarPageRows } from './hooks/useSidebarPageRows'

interface EmbeddedSidebarProps {
  siteID: string | undefined
  orgID: string | undefined
  orgOrSiteID: string | undefined
}

const ENTRY_BROWSER_TAB_LABEL = 'Entry Browser'
const BUG_REPORT_OPEN_ACTION = 'Opened bug report from embedded sidebar'
export function EmbeddedSidebar({
  siteID,
  orgID,
  orgOrSiteID,
}: EmbeddedSidebarProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const [isNotificationPreferencesOpen, setIsNotificationPreferencesOpen] =
    useState(false)
  const { t } = useLingui()
  const {
    allowedNavbarItems,
    chats: showChats,
    workspaceTabs: workspaceTabsEnabled,
  } = useNavbarConfig({
    orgId: orgID ?? '',
    siteId: siteID,
  })
  const {
    site,
    sites,
    sitesForOrg,
    organization,
    organizations,
    isLoading,
    hasEverLoaded,
  } = useNavbarData(orgOrSiteID)
  const selectorCapabilities = useSiteSelectorCapabilities(organization?.id)
  const showBrandHomeLink = shouldShowEmbeddedSidebarBrand({
    isLoading,
    hasEverLoaded,
    hasLoadedPermissions: selectorCapabilities.hasLoadedPermissions,
    organizationIds: organizations.map(({ id }) => id),
    siteIdsForSelectedOrganization: sitesForOrg.map(({ id }) => id),
    selectedOrganizationId: organization?.id,
    selectedSiteId: site?.id,
    capabilities: selectorCapabilities,
  })

  const { setOrgAndSiteIds } = useSelectedOrgAndSiteInterface()
  const { isMobile, setOpenMobile } = useSidebar()
  const workspaceTabs = useOptionalWorkspaceTabs()
  const openDestinationTab = workspaceTabsEnabled
    ? workspaceTabs?.openDestinationTab
    : undefined
  const activeWorkspaceTab = workspaceTabs?.openTabs.find(
    (tab) => tab.id === workspaceTabs.activeTabId
  )
  const activeHistoryItem =
    activeWorkspaceTab?.history[activeWorkspaceTab.currentIndex]
  const activeDestination =
    activeHistoryItem?.type === 'destination'
      ? Object.values(SelectedTab).find(
          (tab) => tab === activeHistoryItem.destinationKey
        )
      : activeWorkspaceTab?.workspaceDestination
  const selectedTab = openDestinationTab
    ? showChats
      ? activeDestination
      : undefined
    : getSelectedTab(location.pathname + location.search)
  const mountChatSessions = showChats || workspaceTabsEnabled
  const { hasPermissions, user } = useGeckoPermissions()
  const canVisitSiteView = useCanVisitSiteView()
  const isInternalUser = hasPermissions([PERMISSION_GECKO_INTERNAL_MEMBER])
  const cmmsSiteConfig = useCmmsExplorerSiteConfig(
    isInternalUser ? site?.id : undefined
  )
  const isExternalAudience = !isInternalUser
  const visibleNavItems = getVisibleSidebarNavItems({
    canVisitSiteView,
    chatLayout: showChats,
    hasPermissions,
    navItemKeys: allowedNavbarItems,
    siteID,
  })
  const navItems = withCmmsExplorerNavItem(
    visibleNavItems,
    isInternalUser && Boolean(site?.id) && cmmsSiteConfig.enabled
  )
  const pageRows = useSidebarPageRows(navItems.length, showChats)
  const canResizePages = showChats && pageRows.canResize

  const closeMobileSidebar = useCallback(() => {
    if (isMobile) setOpenMobile(false)
  }, [isMobile, setOpenMobile])

  const navigateToDestination = useCallback(
    async (destinationKey: string, label: string, href: string) => {
      await setOrgAndSiteIds({
        organizationId: orgID,
        siteId: siteID,
      })

      if (openDestinationTab) {
        openDestinationTab({
          destinationKey,
          label,
          href,
        })
        closeMobileSidebar()
        return
      }

      await navigate({ href })
      closeMobileSidebar()
    },
    [
      closeMobileSidebar,
      navigate,
      openDestinationTab,
      orgID,
      siteID,
      setOrgAndSiteIds,
    ]
  )

  const onNavigateItemSelected = useCallback(
    async (
      event: MouseEvent<HTMLAnchorElement>,
      destinationKey: string,
      label: string
    ) => {
      event.stopPropagation()
      event.preventDefault()
      const destinationUrl = new URL(event.currentTarget.href)
      await navigateToDestination(
        destinationKey,
        label,
        `${destinationUrl.pathname}${destinationUrl.search}${destinationUrl.hash}`
      )
    },
    [navigateToDestination]
  )

  const onFooterWorkspaceNavigate = useCallback(
    (
      event: MouseEvent<HTMLAnchorElement>,
      info: { label: string; destinationKey: string }
    ) => {
      void onNavigateItemSelected(event, info.destinationKey, info.label)
    },
    [onNavigateItemSelected]
  )

  const handleOpenUpload = useCallback(() => {
    if (!site?.id) return
    if (openDestinationTab) {
      const search = new URLSearchParams({
        [SEARCH_PARAM_SITE_ID]: site.id,
      })
      openDestinationTab({
        destinationKey: WORKSPACE_SETTINGS_DESTINATION.uploadDocs,
        label: WORKSPACE_SETTINGS_DESTINATION.uploadDocs,
        href: `/documents/upload?${search.toString()}`,
      })
      closeMobileSidebar()
      return
    }
    void navigate({
      to: '/documents/upload',
      search: { [SEARCH_PARAM_SITE_ID]: site.id },
    })
    closeMobileSidebar()
  }, [closeMobileSidebar, navigate, openDestinationTab, site?.id])

  const buildSidebarBugReportContext = useCallback(
    (action: string): ViewerBugReportContext => ({
      reportedByEmail: user?.email,
      siteId: site?.id ?? '',
      recentActions: [action],
    }),
    [site?.id, user?.email]
  )

  const handleEntryBrowserClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      void onNavigateItemSelected(
        event,
        SelectedTab.EntryBrowser,
        ENTRY_BROWSER_TAB_LABEL
      )
    },
    [onNavigateItemSelected]
  )

  return (
    <Sidebar
      collapsible='icon'
      variant='embedded'
      layout={showChats ? 'anchored' : 'default'}>
      <SidebarHeader>
        <Box width='full' minWidth='none'>
          <SiteSelectorsSidebar
            selectedOrganization={organization}
            organizations={organizations}
            selectedSite={site}
            sites={sites}
            isLoading={isLoading}
            hasEverLoaded={hasEverLoaded}
            onWorkspaceNavigate={onFooterWorkspaceNavigate}
            showBrandHomeLink={showBrandHomeLink}
          />
        </Box>
      </SidebarHeader>
      <SidebarSeparator />
      <Box asChild overflow='hidden'>
        <SidebarContent>
          <Box
            display='flex'
            direction='column'
            flex='one'
            gap='none'
            width='full'
            height='full'
            minHeight='none'
            overflow='hidden'>
            {showChats ? (
              <SidebarNewChatButton siteId={siteID} />
            ) : (
              <SidebarSearchMenuRow />
            )}
            <SidebarPagesPane
              ref={pageRows.pagesPaneRef}
              $chatLayout={showChats}>
              <EmbeddedSidebarHomeNav
                chatLayout={showChats}
                maxRows={showChats ? pageRows.rows : undefined}
                navItems={navItems}
                onNavigateDestination={navigateToDestination}
                onNavigateItemSelected={onNavigateItemSelected}
                orgID={orgID}
                selectedTab={selectedTab}
                siteID={siteID}
              />
            </SidebarPagesPane>
            {canResizePages ? (
              <EmbeddedSidebarPagesSplitHandle
                maxRows={navItems.length}
                onKeyDown={pageRows.handleKeyDown}
                onPointerDown={pageRows.handlePointerDown}
                rows={pageRows.rows}
              />
            ) : null}
            {mountChatSessions ? (
              <EmbeddedSidebarChatHistory
                openTabs={workspaceTabs?.openTabs}
                showHistory={showChats}
                siteId={siteID}
              />
            ) : null}
          </Box>
        </SidebarContent>
      </Box>
      <SidebarFooter>
        <SidebarMenu>
          <PermissionsGate requires={VIEWER_BUG_REPORT_ALLOWED_ROLES}>
            <DocumentsBugReportButton
              buildContext={buildSidebarBugReportContext}
              openActionLabel={BUG_REPORT_OPEN_ACTION}
              dialogTitle={t`Give Feedback`}
              renderTrigger={({ disabled, onClick }) => (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    type='button'
                    disabled={disabled || !site?.id}
                    variant='embedded'
                    tooltip={t`Give Feedback`}
                    aria-label={t`Give Feedback`}
                    onClick={onClick}>
                    <LucideIcon name='Flag' />
                    <span>
                      <Trans>Give Feedback</Trans>
                    </span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
            />
          </PermissionsGate>
          {!isExternalAudience && (
            <PermissionsGate requires={[PERMISSION_GECKO_INTERNAL_MEMBER]}>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={selectedTab === SelectedTab.EntryBrowser}
                  tooltip={showChats ? t`Entry Browser` : undefined}
                  variant='embedded'>
                  <Link
                    to='/entry-browser'
                    title={showChats ? undefined : t`Entry Browser`}
                    onClick={handleEntryBrowserClick}>
                    <LucideIcon name='Database' />
                    <span>
                      <Trans>Entry Browser</Trans>
                    </span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </PermissionsGate>
          )}
          {!isExternalAudience && (
            <ConfigSettingsSidebar
              orgID={organization?.id}
              siteID={site?.id}
              onWorkspaceNavigate={onFooterWorkspaceNavigate}
            />
          )}
          {!isExternalAudience && <NotificationsDropdownSidebar />}
          <ProfileAndSettingsSidebar
            orgID={organization?.id}
            siteID={site?.id}
            onWorkspaceNavigate={onFooterWorkspaceNavigate}
            onOpenNotificationPreferences={() =>
              setIsNotificationPreferencesOpen(true)
            }
            onOpenUploadDocuments={site?.id ? handleOpenUpload : undefined}
            showNotificationPreferences={!isExternalAudience}
          />
        </SidebarMenu>
      </SidebarFooter>
      <SidebarSeparator />
      <SidebarFooter>
        <EmbeddedSidebarFooterBar
          chatLayout={showChats}
          showAIRFooter={showChats || !showBrandHomeLink}
        />
      </SidebarFooter>
      <NotificationPreferencesModal
        isOpen={isNotificationPreferencesOpen}
        onClose={() => setIsNotificationPreferencesOpen(false)}
      />
    </Sidebar>
  )
}
